import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Grid, Paper, Skeleton, Alert, FormControl, InputLabel,
  Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, Area, AreaChart, BarChart, Bar
} from 'recharts';
import { Warning, TrendingUp } from '@mui/icons-material';
import { especializadaService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';

const EspecializadaPredicao: React.FC = () => {
  const { municipioAtivo } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [especialidade, setEspecialidade] = useState<string>('');

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    setLoading(true);
    setError(null);
    especializadaService.getPredicao(municipioAtivo.id, especialidade || undefined)
      .then(setData)
      .catch((err: any) => setError(err.response?.data?.message || 'Erro ao carregar dados'))
      .finally(() => setLoading(false));
  }, [municipioAtivo?.id, especialidade]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const historico = (data.serieHistorica || []).map((s: any) => ({
      mes: new Date(s.mes).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
      mesDate: new Date(s.mes),
      historico: Number(s.total_atendimentos),
      tipo: 'historico',
    }));
    const predicoes = (data.predicoes || []).map((p: any) => ({
      mes: new Date(p.mes_referencia).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
      mesDate: new Date(p.mes_referencia),
      projecao: Number(p.demanda_prevista),
      limite_inferior: Number(p.limite_inferior),
      limite_superior: Number(p.limite_superior),
      tipo: 'projecao',
    }));

    // Conectar último ponto histórico com primeiro da projeção
    if (historico.length > 0 && predicoes.length > 0) {
      predicoes[0].historico = historico[historico.length - 1].historico;
    }

    return [...historico, ...predicoes];
  }, [data]);

  const alertas = useMemo(() => {
    return (data?.alertasTendencia || []).filter((a: any) => Number(a.tendencia_media) > 4);
  }, [data]);

  const dadosContexto = useMemo(() => {
    if (!data) return '';
    const topAlertas = alertas.slice(0, 5);
    return `Predicao de Demanda - ${municipioAtivo?.nome || 'Contagem/MG'}
Especialidade selecionada: ${especialidade || 'Todas'}
Serie historica: ${(data.serieHistorica || []).length} meses
Projecoes: ${(data.predicoes || []).length} pontos
Alertas (tendencia >4%): ${topAlertas.map((a: any) => a.especialidade + ': +' + a.tendencia_media + '%').join(', ') || 'Nenhum'}`;
  }, [data, especialidade, alertas, municipioAtivo]);

  if (loading) return <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={500} /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Predição de Demandas</Typography>
          <Typography variant="body2" color="text.secondary">Série histórica e projeção futura com IA</Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel>Especialidade</InputLabel>
          <Select value={especialidade} onChange={e => setEspecialidade(e.target.value)} label="Especialidade">
            <MenuItem value="">Todas as especialidades</MenuItem>
            {(data?.especialidadesDisponiveis || []).map((e: string) => (
              <MenuItem key={e} value={e}>{e}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}><KpiCard titulo="Meses Histórico" valor={(data?.serieHistorica || []).length} cor="#2563eb" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Meses Projeção" valor={Math.ceil((data?.predicoes || []).length / Math.max((data?.especialidadesDisponiveis || []).length, 1))} cor="#7c3aed" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Especialidades" valor={(data?.especialidadesDisponiveis || []).length} cor="#16a34a" /></Grid>
        <Grid item xs={6} sm={3}><KpiCard titulo="Alertas Tendência" valor={alertas.length} cor={alertas.length > 0 ? '#ef4444' : '#16a34a'} /></Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <AnalisePredicaoCard manualTrigger={true}
          municipioId={municipioAtivo?.id || 0}
          contexto="dashboard"
          dadosContexto={dadosContexto}
          prompt="Voce e um analista de planejamento em saude com foco em modelos preditivos. Analise a serie historica e projecoes. Gere: 1) Tendencias identificadas (crescimento/estabilidade/reducao), 2) Especialidades criticas com maior crescimento, 3) Recomendacoes de adequacao da oferta, 4) Riscos para os proximos 6 meses. Seja conciso."
        />
      </Box>

      <Grid container spacing={2}>
        {/* Gráfico Principal - Histórico + Projeção */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Série Histórica + Projeção {especialidade ? `— ${especialidade}` : '(Consolidado)'}
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" fontSize={10} angle={-45} textAnchor="end" height={50} />
                <YAxis fontSize={11} tickFormatter={(v: any) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
                <Tooltip formatter={(v: any, name: any) => [v.toLocaleString('pt-BR'), name === 'historico' ? 'Histórico' : name === 'projecao' ? 'Projeção' : name === 'limite_superior' ? 'Limite Superior' : 'Limite Inferior']} />
                <Legend />
                <Area type="monotone" dataKey="limite_superior" name="Intervalo Confiança" stroke="none" fill="#7c3aed" fillOpacity={0.1} />
                <Area type="monotone" dataKey="limite_inferior" name="" stroke="none" fill="#fff" fillOpacity={1} />
                <Line type="monotone" dataKey="historico" name="Histórico" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
                <Line type="monotone" dataKey="projecao" name="Projeção" stroke="#7c3aed" strokeWidth={2} strokeDasharray="8 4" dot={{ r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Alertas de Tendência */}
        {alertas.length > 0 && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color="warning" /> Alertas de Tendência (&gt;4% crescimento)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={alertas.map((a: any) => ({
                  especialidade: a.especialidade?.substring(0, 18),
                  tendencia: Number(a.tendencia_media),
                }))} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" fontSize={11} unit="%" />
                  <YAxis type="category" dataKey="especialidade" width={140} fontSize={10} />
                  <Tooltip formatter={(v: any) => [`${v}%`, 'Tendência']} />
                  <Bar dataKey="tendencia" fill="#ef4444" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}

        {/* Tabela de Projeções */}
        <Grid item xs={12} md={alertas.length > 0 ? 6 : 12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Projeções por Especialidade</Typography>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Especialidade</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Mês</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Previsto</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Intervalo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Tendência</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.predicoes || []).slice(0, 50).map((p: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{p.especialidade}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{new Date(p.mes_referencia).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{Number(p.demanda_prevista).toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{Number(p.limite_inferior).toLocaleString('pt-BR')} - {Number(p.limite_superior).toLocaleString('pt-BR')}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" icon={<TrendingUp sx={{ fontSize: 14 }} />}
                          label={`+${p.tendencia_pct}%`}
                          color={Number(p.tendencia_pct) > 5 ? 'error' : Number(p.tendencia_pct) > 3 ? 'warning' : 'success'}
                          variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Evolução Fila de Espera */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Evolução da Fila de Espera</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={(data?.evolucaoFilaEspera || []).map((f: any) => ({
                mes: new Date(f.mes).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
                solicitacoes: Number(f.novas_solicitacoes),
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" fontSize={10} />
                <YAxis fontSize={11} tickFormatter={(v: any) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(v)} />
                <Tooltip formatter={(v: any) => [v.toLocaleString('pt-BR'), 'Novas Solicitações']} />
                <Line type="monotone" dataKey="solicitacoes" stroke="#ef4444" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      <ChatIAButton contexto="dashboard" dadosContexto={dadosContexto} />
    </Box>
  );
};

export default EspecializadaPredicao;
